import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Button,
  Skeleton,
  SimpleGrid,
  ThemeIcon,
  Progress,
  Badge,
  Divider,
  Card,
  Modal,
  Image,
  CopyButton,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconTruck,
  IconPackage,
  IconCalendar,
  IconMapPin,
  IconClock,
  IconClipboardCheck,
  IconPhone,
  IconPhoto,
  IconSignature,
  IconShare,
  IconTemperature,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import OrderTimeline from '@/components/OrderTimeline';
import EmptyState from '@/components/EmptyState';
import LiveTrackingMap from '@/components/LiveTrackingMap';
import {
  useTracking,
  useOrder,
  useEnhancedTracking,
  useProofOfDelivery,
  useShareTrackingLink,
} from '@/hooks/usePortalApi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const STATUS_PROGRESS: Record<string, number> = {
  draft: 0,
  pending: 5,
  confirmed: 20,
  processing: 35,
  picking: 45,
  packed: 55,
  shipped: 75,
  delivered: 100,
  invoiced: 100,
};

export default function OrderTracking() {
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id) || 0;
  const { data: tracking, isLoading: trackingLoading, isError } = useTracking(orderId);
  const { data: order, isLoading: orderLoading } = useOrder(orderId);
  const { data: enhanced } = useEnhancedTracking(orderId);
  const { data: pod } = useProofOfDelivery(orderId);
  const shareLink = useShareTrackingLink();

  const [podOpen, setPodOpen] = useState(false);

  const isLoading = trackingLoading || orderLoading;

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={30} width={200} />
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
        </SimpleGrid>
        <Skeleton height={400} />
      </Stack>
    );
  }

  const status = tracking?.status || order?.status || 'pending';
  const progress = STATUS_PROGRESS[status] ?? 0;
  const lastEvent = tracking?.events?.[tracking.events.length - 1];
  const isDelivered = status === 'delivered' || status === 'invoiced';

  return (
    <Stack gap="lg">
      <Group>
        <Button
          component={Link}
          to={`/orders/${id}`}
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          size="sm"
          color="gray"
        >
          Back to Order
        </Button>
      </Group>

      <PageHeader
        title="Order Tracking"
        description={tracking?.order_number || order?.order_number || `Order #${id}`}
        actions={
          <>
            <StatusBadge status={status} />
            <CopyButton value={shareLink.data?.data.tracking_url || ''}>
              {({ copied, copy }) => (
                <Button
                  variant="light"
                  leftSection={<IconShare size={16} />}
                  size="sm"
                  color={copied ? 'green' : 'blue'}
                  onClick={() => {
                    shareLink.mutate(orderId, { onSuccess: () => copy() });
                  }}
                  loading={shareLink.isPending}
                >
                  {copied ? 'Copied!' : 'Share'}
                </Button>
              )}
            </CopyButton>
            {pod && (
              <Button
                variant="light"
                color="teal"
                leftSection={<IconPhoto size={16} />}
                size="sm"
                onClick={() => setPodOpen(true)}
              >
                View POD
              </Button>
            )}
          </>
        }
      />

      {/* Progress Bar */}
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>Order Progress</Text>
          <Text size="sm" c="dimmed">{progress}%</Text>
        </Group>
        <Progress
          value={progress}
          size="lg"
          radius="xl"
          color={isDelivered ? 'green' : 'blue'}
          animated={!isDelivered && progress > 0}
        />
        <Group justify="space-between" mt="xs">
          <Text size="xs" c="dimmed">Placed</Text>
          <Text size="xs" c="dimmed">Confirmed</Text>
          <Text size="xs" c="dimmed">Shipped</Text>
          <Text size="xs" c="dimmed">Delivered</Text>
        </Group>
      </Paper>

      {/* Live GPS Map */}
      {['shipped', 'delivered', 'invoiced'].includes(status) && (
        <LiveTrackingMap orderId={orderId} />
      )}

      {/* Info Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
        {/* ETA Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color={isDelivered ? 'green' : 'blue'} size="lg">
              <IconCalendar size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">
                {isDelivered ? 'Delivered On' : 'Estimated Delivery'}
              </Text>
              <Text size="lg" fw={700}>
                {tracking?.estimated_delivery
                  ? dayjs(tracking.estimated_delivery).format('ddd, MMM D')
                  : order?.delivery_date
                    ? dayjs(order.delivery_date).format('ddd, MMM D')
                    : 'Pending'}
              </Text>
            </div>
          </Group>
          {!isDelivered && tracking?.estimated_delivery && (
            <Badge variant="light" color="blue" size="sm">
              {dayjs(tracking.estimated_delivery).fromNow()}
            </Badge>
          )}
        </Card>

        {/* Shipment Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="violet" size="lg">
              <IconTruck size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Shipment</Text>
              <Text size="lg" fw={700}>
                {['shipped', 'delivered', 'invoiced'].includes(status)
                  ? 'In Transit'
                  : status === 'packed'
                    ? 'Ready to Ship'
                    : 'Processing'}
              </Text>
            </div>
          </Group>
          {order && (
            <Text size="xs" c="dimmed">
              {(order as any).item_count || order.lines?.length || 0} item{((order as any).item_count || order.lines?.length || 0) !== 1 ? 's' : ''}
              {' | '}${parseFloat(String(order.total)).toFixed(2)}
            </Text>
          )}
        </Card>

        {/* Last Update Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="orange" size="lg">
              <IconClock size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Last Update</Text>
              <Text size="lg" fw={700}>
                {lastEvent
                  ? dayjs(lastEvent.timestamp).fromNow()
                  : 'No updates yet'}
              </Text>
            </div>
          </Group>
          {lastEvent?.location && (
            <Group gap={4}>
              <IconMapPin size={12} color="gray" />
              <Text size="xs" c="dimmed">{lastEvent.location}</Text>
            </Group>
          )}
        </Card>
      </SimpleGrid>

      {/* Proof of Delivery */}
      {pod && (
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Proof of Delivery</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
            <Group gap="xs">
              <IconSignature size={16} color="gray" />
              <Text size="sm" c="dimmed">Signed By:</Text>
              <Text size="sm" fw={500}>{pod.signed_by}</Text>
            </Group>
            <Group gap="xs">
              <IconPackage size={16} color="gray" />
              <Text size="sm" c="dimmed">Condition:</Text>
              <Text size="sm" fw={500}>{pod.delivery_condition}</Text>
            </Group>
            {pod.temperature != null && (
              <Group gap="xs">
                <IconTemperature size={16} color="gray" />
                <Text size="sm" c="dimmed">Temperature:</Text>
                <Text size="sm" fw={500}>{pod.temperature}°F</Text>
              </Group>
            )}
            {pod.damage_notes && (
              <Group gap="xs">
                <IconClipboardCheck size={16} color="gray" />
                <Text size="sm" c="dimmed">Damage Notes:</Text>
                <Text size="sm" fw={500} c="red">{pod.damage_notes}</Text>
              </Group>
            )}
          </SimpleGrid>

          {pod.photo_urls && pod.photo_urls.length > 0 && (
            <>
              <Text size="sm" fw={500} mt="md" mb="xs">Delivery Photos</Text>
              <Group gap="sm">
                {pod.photo_urls.map((url: string, idx: number) => (
                  <Image
                    key={idx}
                    src={url}
                    alt={`Delivery photo ${idx + 1}`}
                    width={120}
                    height={90}
                    radius="md"
                    fit="cover"
                  />
                ))}
              </Group>
            </>
          )}

          {pod.signature_data && (
            <>
              <Text size="sm" fw={500} mt="md" mb="xs">Signature</Text>
              <Image
                src={pod.signature_data}
                alt="Delivery signature"
                width={240}
                height={80}
                radius="md"
                fit="contain"
              />
            </>
          )}
        </Paper>
      )}

      {/* Delivery Stop Info */}
      {enhanced?.stop && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="grape" size="lg">
              <IconMapPin size={20} />
            </ThemeIcon>
            <Text fw={600}>Delivery Stop</Text>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <div>
              <Text size="xs" c="dimmed">Planned Arrival</Text>
              <Text size="sm" fw={500}>
                {dayjs(enhanced.stop.planned_arrival).format('MMM D, YYYY [at] h:mm A')}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Actual Arrival</Text>
              <Text size="sm" fw={500}>
                {enhanced.stop.actual_arrival
                  ? dayjs(enhanced.stop.actual_arrival).format('MMM D, YYYY [at] h:mm A')
                  : 'Pending'}
              </Text>
            </div>
          </SimpleGrid>
        </Card>
      )}

      {/* POD Detail Modal */}
      <Modal opened={podOpen} onClose={() => setPodOpen(false)} title="Proof of Delivery" size="lg">
        {pod && (
          <Stack gap="md">
            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Signed By</Text>
                <Text size="sm" fw={500}>{pod.signed_by}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Delivery Condition</Text>
                <Text size="sm" fw={500}>{pod.delivery_condition}</Text>
              </div>
              {pod.temperature != null && (
                <div>
                  <Text size="xs" c="dimmed">Temperature</Text>
                  <Text size="sm" fw={500}>{pod.temperature}°F</Text>
                </div>
              )}
              {pod.damage_notes && (
                <div>
                  <Text size="xs" c="dimmed">Damage Notes</Text>
                  <Text size="sm" fw={500} c="red">{pod.damage_notes}</Text>
                </div>
              )}
            </SimpleGrid>
            {pod.photo_urls && pod.photo_urls.length > 0 && (
              <>
                <Divider />
                <Text fw={500}>Photos</Text>
                <Group gap="sm">
                  {pod.photo_urls.map((url: string, idx: number) => (
                    <Image
                      key={idx}
                      src={url}
                      alt={`Delivery photo ${idx + 1}`}
                      width={200}
                      height={150}
                      radius="md"
                      fit="cover"
                    />
                  ))}
                </Group>
              </>
            )}
            {pod.signature_data && (
              <>
                <Divider />
                <Text fw={500}>Signature</Text>
                <Image
                  src={pod.signature_data}
                  alt="Delivery signature"
                  width={300}
                  height={100}
                  radius="md"
                  fit="contain"
                />
              </>
            )}
          </Stack>
        )}
      </Modal>

      {/* Order Details */}
      {order && (
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Order Details</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Group gap="xs">
              <IconClipboardCheck size={16} color="gray" />
              <Text size="sm" c="dimmed">Order Number:</Text>
              <Text size="sm" fw={500}>{order.order_number}</Text>
            </Group>
            <Group gap="xs">
              <IconCalendar size={16} color="gray" />
              <Text size="sm" c="dimmed">Placed:</Text>
              <Text size="sm" fw={500}>
                {dayjs(order.created_at).format('MMM D, YYYY [at] h:mm A')}
              </Text>
            </Group>
            <Group gap="xs">
              <IconPackage size={16} color="gray" />
              <Text size="sm" c="dimmed">Items:</Text>
              <Text size="sm" fw={500}>
                {(order as any).item_count || order.lines?.length || 0}
              </Text>
            </Group>
            <Group gap="xs">
              <IconPhone size={16} color="gray" />
              <Text size="sm" c="dimmed">Support:</Text>
              <Text size="sm" fw={500} c="blue">Contact us for help</Text>
            </Group>
          </SimpleGrid>
        </Paper>
      )}

      <Divider />

      {/* Timeline */}
      <Paper p="lg" withBorder>
        <Text fw={600} mb="md">Tracking History</Text>
        {isError || !tracking?.events?.length ? (
          <EmptyState
            title="No tracking info yet"
            description="Tracking information will appear once your order is processed. Check back soon!"
          />
        ) : (
          <OrderTimeline events={tracking.events} />
        )}
      </Paper>
    </Stack>
  );
}
